import { useEffect, useMemo, useState } from "react";
import { useSearchParams, useParams, Link } from "react-router-dom";
import api from "../lib/api";
import { ProductCard } from "../components/ProductCard";
import { ProductFilters } from "../components/ProductFilters";
import { ChevronLeft, ChevronRight } from "lucide-react";

export default function Shop() {
  const { category } = useParams();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState({ items: [], total: 0, page: 1, limit: 24, pages: 1 });
  const [loading, setLoading] = useState(true);
  const [categoryName, setCategoryName] = useState("All Pieces");

  // Build query string for backend
  const queryString = useMemo(() => {
    const u = new URLSearchParams();
    if (category) u.set("category", category);
    for (const [k, v] of params.entries()) {
      if (k === "available_on_rikshaw_any") continue;
      u.set(k, v);
    }
    if (!u.has("sort")) u.set("sort", "new");
    if (!u.has("page")) u.set("page", "1");
    if (!u.has("limit")) u.set("limit", "24");
    return u.toString();
  }, [category, params]);

  useEffect(() => {
    setLoading(true);
    api
      .get(`/products?${queryString}`)
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, [queryString]);

  useEffect(() => {
    if (!category) {
      setCategoryName("All Pieces");
      return;
    }
    api.get(`/categories`).then((r) => {
      const c = (r.data.categories || []).find((x) => x.slug === category);
      setCategoryName(c?.name || category.replace("-", " "));
    });
  }, [category]);

  let items = data.items;
  // post-filter for the "available on a rikshaw (any)" toggle
  if (params.get("available_on_rikshaw_any") === "1") {
    items = items.filter((p) => p.available_on_rikshaw);
  }

  const onSort = (e) => {
    const next = new URLSearchParams(params);
    next.set("sort", e.target.value);
    next.delete("page");
    setParams(next);
  };

  const setPage = (p) => {
    const next = new URLSearchParams(params);
    next.set("page", String(p));
    setParams(next);
  };

  const onSearch = (e) => {
    e.preventDefault();
    const q = e.target.q.value.trim();
    const next = new URLSearchParams(params);
    if (q) next.set("q", q);
    else next.delete("q");
    next.delete("page");
    setParams(next);
  };

  const page = Number(params.get("page") || 1);

  return (
    <div data-testid="shop-page" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
        <div>
          <span className="label-overline text-jow-gold">The Atelier</span>
          <h1
            className="font-display text-4xl md:text-6xl mt-2 text-jow-navy capitalize"
            data-testid="shop-heading"
          >
            {categoryName}
          </h1>
          <p className="mt-2 text-jow-navy/55 text-sm">
            {data.total} pieces · live from our atelier
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
          <form onSubmit={onSearch} className="flex border border-jow-navy/20 bg-white">
            <input
              name="q"
              defaultValue={params.get("q") || ""}
              placeholder="Search…"
              className="px-4 py-3 text-sm outline-none w-44"
              data-testid="shop-search-input"
            />
            <button type="submit" className="px-4 bg-jow-navy text-jow-cream text-xs uppercase tracking-widest" data-testid="shop-search-submit">
              Go
            </button>
          </form>
          <select
            value={params.get("sort") || "new"}
            onChange={onSort}
            className="border border-jow-navy/20 bg-white px-4 py-3 text-sm outline-none"
            data-testid="shop-sort"
          >
            <option value="new">New arrivals</option>
            <option value="price_asc">Price · low to high</option>
            <option value="price_desc">Price · high to low</option>
            <option value="name">Name</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
        <div className="md:col-span-3">
          <ProductFilters category={category} />
        </div>

        <div className="md:col-span-9">
          {loading && (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-5" data-testid="shop-loading">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="aspect-[4/5] bg-white border border-jow-navy/10 animate-pulse" />
              ))}
            </div>
          )}
          {!loading && items.length === 0 && (
            <div className="border border-jow-navy/10 bg-white p-12 text-center" data-testid="shop-empty">
              <p className="font-display text-2xl text-jow-navy">Nothing here yet.</p>
              <p className="text-jow-navy/55 mt-2">Try clearing some filters.</p>
            </div>
          )}
          {!loading && items.length > 0 && (
            <>
              <div
                className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-5"
                data-testid="shop-grid"
              >
                {items.map((p, i) => (
                  <ProductCard key={p.id} product={p} index={i} />
                ))}
              </div>

              {data.pages > 1 && (
                <div
                  className="flex items-center justify-between mt-12"
                  data-testid="shop-pagination"
                >
                  <button
                    type="button"
                    disabled={page <= 1}
                    onClick={() => setPage(page - 1)}
                    className="btn-secondary-jow disabled:opacity-50"
                    data-testid="page-prev"
                  >
                    <ChevronLeft size={14} /> Prev
                  </button>
                  <span className="label-overline text-jow-navy/60">
                    Page {data.page} of {data.pages}
                  </span>
                  <button
                    type="button"
                    disabled={page >= data.pages}
                    onClick={() => setPage(page + 1)}
                    className="btn-secondary-jow disabled:opacity-50"
                    data-testid="page-next"
                  >
                    Next <ChevronRight size={14} />
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
